// React imports
import { useState, useEffect, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import validator from 'email-validator';
import { useOutletContext } from 'react-router-dom';
import CurrencyList from 'currency-list';

// Mui imports
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Autocomplete from '@mui/material/Autocomplete';
import LoadingButton from '@mui/lab/LoadingButton';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import * as colors from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as icons from '@fortawesome/pro-light-svg-icons';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faGear, faLink, faDollarSign, faHeading } from '@fortawesome/pro-light-svg-icons';

// Local imports
import AddressInput from '../AddressInput';
import Header from '../Header';
import OKARGO_PLATFORMS from '../OKARGO_PLATFORMS.json';
import ExchangeRates from './Settings.exchangerates';

// Monada imports
import { Settings as CargoAISettings } from '@monada-ai/monada-cargoai-integration/dist/client';
import { Settings as CargofiveSettings } from '@monada-ai/monada-cargofive-integration/dist/client';

function Settings(props) {
    const { org, accesstoken, seaPorts, onOrgUpdated, reportConfiguration: _reportConfiguration, me, exchangeRates, allAccounts, accounts, setAccounts } = useOutletContext();
    const [ organization, setOrganization ] = useState({
        ...JSON.parse(JSON.stringify(org)),
        xmlMailingList: (org.xmlMailingList || []).join(',')
    });
    const [ reportConfiguration, setReportConfiguration ] = useState(_reportConfiguration || []);
    const [ saving, setSaving ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(null);
    const [ errors, setErrors ] = useState([]);
    const [ screen, setScreen ] = useState('general');
    const theme = useTheme();
    useEffect(() => {
        setReportConfiguration(_reportConfiguration);
    }, [_reportConfiguration]);

    const currencies = useMemo(() => _.values(CurrencyList.getAll('en_US')), []);

    const emailIntegration = _.get(organization, 'integrations.email.enabled', false);
    const setEmailIntegration = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.email.enabled', val)
        setOrganization(o);
    };

    const cargowiseIntegration = _.get(organization, 'integrations.cargowise.enabled', false);
    const setCargowiseIntegration = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.cargowise.enabled', val)
        setOrganization(o);
    };

    const cargofiveIntegration = _.get(organization, 'integrations.cargofive.enabled', false);
    const setCargofiveIntegration = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.cargofive.enabled', val)
        setOrganization(o);
    };

    const cargofiveUseForRates = _.get(organization, 'integrations.cargofive.useForRates', false);
    const setCargofiveUseForRates = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.cargofive.useForRates', val)
        setOrganization(o);
    };

    const okargoIntegration = _.get(organization, 'integrations.okargo.enabled', false);
    const setOkargoIntegration = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.okargo.enabled', val)
        setOrganization(o);
    };

    const webcargoIntegration = _.get(organization, 'integrations.webcargo.enabled', false);
    const setWebcargoIntegration = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.webcargo.enabled', val)
        setOrganization(o);
    };

    const cargoaiIntegration = _.get(organization, 'integrations.cargoai.enabled', false);
    const setCargoAIIntegration = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.cargoai.enabled', val)
        setOrganization(o);
    };

    const opportunityWebhookEnabled = _.get(organization, 'integrations.opportunityWebhook.enabled', false);
    const setOpportunityWebhookEnabled = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.opportunityWebhook.enabled', val);
        setOrganization(o);
    };

    const doorRatesEnabled = _.get(organization, 'integrations.doorRates.enabled', false);
    const setDoorRatesEnabled = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'integrations.doorRates.enabled', val);
        setOrganization(o);
    };

    const [ webhookSecretVisible, setWebhookSecretVisible ] = useState(false);
    const [ webhookCopied, setWebhookCopied ] = useState(false);

    const [ platformsSettings, setPlatformsSettings ] = useState({});
    async function checkOkargoPlatforms({ skipInitChecking } = {}) {
        const platforms = _.get(organization, 'integrations.okargo.platforms', []);
        const token = _.get(organization, 'integrations.okargo.token', '');

        if (!skipInitChecking) {
            setPlatformsSettings(_.fromPairs(platforms.map(c => ([ c.id, { status: 'checking' } ]))));
        }

        try {
            const { data } = await axios.post(`https://app.monada.ai/api/passthroughs/http?organizationId=${org.id}&accesstoken=${accesstoken}`, {
                url: 'https://app.okargo.com/api/CarrierRatesSettings/GetSettings',
                method: 'POST',
                body: {},
                options: {
                    headers: { Authorization: `Bearer ${token}` }
                }
            });

            setPlatformsSettings(_.fromPairs(platforms.map(c => {
                const configurations = data.filter(d => `${d.carrierRatesPlatformId}` === `${c.id}`);

                if (configurations.length === 0) {
                    return [c.id, { status: 'error', description: 'Settings are not updated at Okargo.', data: configurations }];
                }
                if (configurations.length === 1) {
                    return [c.id, { status: 'ok', description: 'Settings seem to be ok.', data: configurations }];
                }
                return [c.id, { status: 'error', description: 'Found multiple settings at Okargo', data: configurations }];
            })));
        } catch (e) {
            setPlatformsSettings(_.fromPairs(platforms.map(c => ([c.id, { status: 'error', description: 'Error checking settings in Okargo. ' + e?.response?.data?.detail, data: [] }]))));
        }
    }

    async function fixOkargoPlatform({ id, settings }) {
        const token = _.get(organization, 'integrations.okargo.token', '');
        setPlatformsSettings(ps => ({ ...ps, [id]: { status: 'checking', data: [] }}));

        // Step 1 - remove existing settings
        for (let i = 0 ; i < platformsSettings[id].data.length ; i++) {
            try {
                await axios.post(`https://app.monada.ai/api/passthroughs/http?organizationId=${org.id}&accesstoken=${accesstoken}`, {
                    url: 'https://app.okargo.com/api/CarrierRatesSettings/Delete',
                    method: 'POST',
                    body: {
                        carrierRatesSettingsId: platformsSettings[id].data[i].carrierRatesSettingsId,
                    }, 
                    options: {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                });
            } catch (e) {
                setPlatformsSettings(ps => ({ ...ps, [id]: { status: 'error', description: 'Error fixing settings in Okargo', data: ps[id].data } }));
                return;
            }
        }

        // Step 2 - add new settings
        try {
            await axios.post(`https://app.monada.ai/api/passthroughs/http?organizationId=${org.id}&accesstoken=${accesstoken}`, {
                url: 'https://app.okargo.com/api/CarrierRatesSettings/Create',
                method: 'POST',
                body: {
                    carrierRatesPlatformId: id,
                    carrierId: OKARGO_PLATFORMS[id].carrierId,
                    carrierRatesSettingsId: 0,
                    login: settings.username,
                    password: settings.password,
                    creationDate: new Date(new Date().setUTCHours(0, 0, 0, 0)).toISOString(),
                    source: 'Admin'
                }, 
                options: {
                    headers: { Authorization: `Bearer ${token}` }
                }
            });
        } catch (e) {
            setPlatformsSettings(ps => ({ ...ps, [id]: { status: 'error', description: 'Error fixing settings in Okargo', data: [] }}));
            return;
        }

        // Step 3 - update validation
        checkOkargoPlatforms({ skipInitChecking: true });
        
    }

    function renderExchangeRates() {
        return <ExchangeRates exchangeRates={exchangeRates} organization={organization} setOrganization={setOrganization} />
    }

    function renderDefaultStrings() {
        return (
            <Box sx={{p: 3}}>
                <TableContainer>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Accept Quote Link Text
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Accept Quote Link Text'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.acceptQuoteLink', 'Click {here} to accept this quote, and we\'ll get started on moving your cargo!')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.acceptQuoteLink', e.target.value);
                                            setOrganization(o);
                                        }}
                                        helperText={'Use {text} as a placeholder for the link, where text will be the clickable link text (e.g., {here} or {view quote})'}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        {'Default text used for the accept quote link in opportunity emails. Use {text} as a placeholder for the link, where text will be the clickable link text (e.g., {here} or {view quote}).'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Email Closing Text
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Email Closing Text'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.emailClosing', 'Best Regards')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.emailClosing', e.target.value);
                                            setOrganization(o);
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        Default closing text used at the end of opportunity emails (e.g., "Best Regards" or "Sincerely").
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Default Email Preface (for email page)
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Default Email Preface'
                                        variant='outlined'
                                        multiline
                                        rows={6}
                                        value={(() => {
                                            const html = _.get(organization, 'defaults.defaultStrings.emailPrefaceForEmailPage', '<div>Hello,</div><div></div><div>[Add your email here]</div><div></div><div>{link}</div><div></div><div>{closing}</div>');
                                            // Convert HTML to plain text for editing - extract content from each div tag
                                            const matches = html.match(/<div>(.*?)<\/div>/g) || [];
                                            return matches.map(match => match.replace(/<\/?div>/g, '')).join('\n');
                                        })()}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            // Convert plain text back to HTML - wrap each line in div tags
                                            const lines = e.target.value.split('\n');
                                            const html = lines.map(line => `<div>${line}</div>`).join('');
                                            _.set(o, 'defaults.defaultStrings.emailPrefaceForEmailPage', html);
                                            setOrganization(o);
                                        }}
                                        helperText={'Use {link} as a placeholder for the accept quote link, and {closing} for the email closing text. Each line will be wrapped in a div tag.'}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        {'Default email preface template. Use {link} for the accept quote link and {closing} for the closing text. Each line will be wrapped in a div tag.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Default Email Body Template
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Default Email Body Template'
                                        variant='outlined'
                                        multiline
                                        rows={8}
                                        value={(() => {
                                            const template = _.get(organization, 'defaults.defaultStrings.emailBodyTemplate', 'Hello,<br/><br/>Thank you for your inquiry.<br/><br/>Further to your request, please find hereby our rates for {transportationMethod}.<br/><br/>Please note that rates are subject to availability of space & equipment at {portType} of loading at time of shipment.<br/><br/>{link}<br/><br/>{closing}');
                                            // Split by <br/> for display
                                            return template.split('<br/>').join('\n');
                                        })()}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            // Join lines back with <br/>
                                            const template = e.target.value.split('\n').join('<br/>');
                                            _.set(o, 'defaults.defaultStrings.emailBodyTemplate', template);
                                            setOrganization(o);
                                        }}
                                        helperText={'Use {transportationMethod} for transportation method, {portType} for port/airport, {link} for accept quote link, and {closing} for closing text.'}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        {'Default email body template for opportunity emails. Use {transportationMethod}, {portType}, {link}, and {closing} as placeholders.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Transportation Method Translations
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Sea Transportation'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.transportationMethodTranslations.sea', 'ocean cargo')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.transportationMethodTranslations.sea', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Air Transportation'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.transportationMethodTranslations.air', 'air cargo')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.transportationMethodTranslations.air', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Land Transportation'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.transportationMethodTranslations.land', 'land transport')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.transportationMethodTranslations.land', e.target.value);
                                            setOrganization(o);
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        Translations for transportation methods used in email templates.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Port Type Translations
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Sea Port Type'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.portTypeTranslations.sea', 'port')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.portTypeTranslations.sea', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Air Port Type'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.portTypeTranslations.air', 'airport')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.portTypeTranslations.air', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Land Port Type'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.portTypeTranslations.land', 'port')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.portTypeTranslations.land', e.target.value);
                                            setOrganization(o);
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        Translations for port/airport types used in email templates.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Subject Transportation Method (Contract)
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Sea (Contract)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.subjectTransportationMethodContract.sea', 'Ocean')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.subjectTransportationMethodContract.sea', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Air (Contract)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.subjectTransportationMethodContract.air', 'Air')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.subjectTransportationMethodContract.air', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Land (Contract)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.subjectTransportationMethodContract.land', 'Land')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.subjectTransportationMethodContract.land', e.target.value);
                                            setOrganization(o);
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        Transportation method translations for contract subject lines.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Subject Transportation Method (One-off)
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Sea (One-off)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.subjectTransportationMethodOneOff.sea', 'Ocean Cargo')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.subjectTransportationMethodOneOff.sea', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Air (One-off)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.subjectTransportationMethodOneOff.air', 'Air Cargo')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.subjectTransportationMethodOneOff.air', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Land (One-off)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.subjectTransportationMethodOneOff.land', 'Land Transport')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.subjectTransportationMethodOneOff.land', e.target.value);
                                            setOrganization(o);
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        Transportation method translations for one-off subject lines.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Email Subject Templates
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        fullWidth
                                        label='Contract Subject Template'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.emailSubjectTemplateContract', '{transportationMethod} Contract Quotation #{id}')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.emailSubjectTemplateContract', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                        helperText={'Use {transportationMethod} and {id} as placeholders'}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Subject Template (with routes)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.emailSubjectTemplateWithRoutes', '{transportationMethod} from {source} to {destination} - Quotation #{id}')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.emailSubjectTemplateWithRoutes', e.target.value);
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                        helperText={'Use {transportationMethod}, {source}, {destination}, and {id} as placeholders'}
                                    />
                                    <TextField
                                        fullWidth
                                        label='Subject Template (default)'
                                        variant='outlined'
                                        value={_.get(organization, 'defaults.defaultStrings.emailSubjectTemplateDefault', '{transportationMethod} - Quotation #{id}')}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'defaults.defaultStrings.emailSubjectTemplateDefault', e.target.value);
                                            setOrganization(o);
                                        }}
                                        helperText={'Use {transportationMethod} and {id} as placeholders'}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        {'Email subject templates for opportunity emails. Use placeholders like {transportationMethod}, {source}, {destination}, and {id}.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    const bookingsEnabled = _.get(organization, 'settings.bookings.enabled', false);
    const setBookingsEnabled = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'settings.bookings.enabled', val);
        setOrganization(o);
    };

    const signatureEnabled = _.get(organization, 'settings.signature.enabled', false);
    const setSignatureEnabled = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'settings.signature.enabled', val);
        setOrganization(o);
    };

    const filterInactiveAccounts = _.get(organization, 'settings.accounts.filterInactiveAccounts', false);
    const setFilterInactiveAccounts = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'settings.accounts.filterInactiveAccounts', val);
        setOrganization(o);
    };

    const filterGraceDays = _.get(organization, 'settings.accounts.filterGraceDays', 0);
    const setFilterGraceDays = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'settings.accounts.filterGraceDays', parseInt(val) || 0);
        setOrganization(o);
    };

    const followUpMinDays = _.get(organization, 'settings.followUp.minDays', 3);
    const setFollowUpMinDays = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'settings.followUp.minDays', parseInt(val) || 0);
        setOrganization(o);
    };

    const followUpMaxDays = _.get(organization, 'settings.followUp.maxDays', 14);
    const setFollowUpMaxDays = (val) => {
        const o = JSON.parse(JSON.stringify(organization));
        _.set(o, 'settings.followUp.maxDays', parseInt(val) || 0);
        setOrganization(o);
    };

    const [ activatingAccount, setActivatingAccount ] = useState(null);
    const [ activateSuccess, setActivateSuccess ] = useState(null);

    // Filtered-out accounts: in allAccounts but not in accounts
    const filteredOutAccounts = useMemo(() => {
        if (!allAccounts || !accounts || !filterInactiveAccounts) return [];
        const activeIds = new Set(accounts.map(a => a.id));
        return allAccounts.filter(a => !activeIds.has(a.id));
    }, [allAccounts, accounts, filterInactiveAccounts]);

    async function onActivateAccount(account) {
        if (!account) return;
        setActivatingAccount(account);
        setActivateSuccess(null);
        try {
            const log = [...(account.log || []), { type: 'activated', timestamp: new Date().getTime(), by: me.email }];
            await axios.put(`/api/accounts/${account.id}?organizationId=${org.id}&accesstoken=${accesstoken}`, {
                account: {
                    name: account.name,
                    tags: account.tags || [],
                    reminders: account.reminders || [],
                    owner: account.owner || undefined,
                    log,
                }
            });
            // Move account from filtered-out to active list
            setAccounts(prev => [...prev, { ...account, log }]);
            setActivateSuccess(account.name || account.id);
        } catch (e) {
            console.error('[Settings] Error activating account:', e);
            setActivateSuccess('error');
        }
        setActivatingAccount(null);
    }

    function renderAccounts() {
        return (
            <Box sx={{ p: 3 }}>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                        {filterInactiveAccounts && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!filterInactiveAccounts && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                Filter Inactive Accounts
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Hide accounts that only exist as CW1 mirrors with no real user activity.
                                Keeps accounts that have timeline updates, log entries, tasks, linked opportunities, linked sales targets, or a real user as owner.
                            </Typography>
                        </Box>
                        <Switch checked={filterInactiveAccounts} onChange={() => setFilterInactiveAccounts(!filterInactiveAccounts)} />
                    </Box>
                </Paper>
                {filterInactiveAccounts && (
                    <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                        <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                    Grace Period for New Accounts
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                    Newly created accounts are exempt from filtering for this many days. Set to 0 to filter immediately.
                                </Typography>
                            </Box>
                            <Select
                                size='small'
                                value={filterGraceDays}
                                onChange={(e) => setFilterGraceDays(e.target.value)}
                                sx={{ minWidth: 100 }}
                            >
                                <MenuItem value={0}>No grace</MenuItem>
                                <MenuItem value={7}>7 days</MenuItem>
                                <MenuItem value={14}>14 days</MenuItem>
                                <MenuItem value={30}>30 days</MenuItem>
                                <MenuItem value={60}>60 days</MenuItem>
                                <MenuItem value={90}>90 days</MenuItem>
                            </Select>
                        </Box>
                    </Paper>
                )}
                {filterInactiveAccounts && (
                    <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                        <Box sx={{ p: 1 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                Restore Filtered Account
                            </Typography>
                            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                Search for an account that was removed by the filter and restore it to your workspace.
                            </Typography>
                            <Autocomplete
                                options={filteredOutAccounts}
                                getOptionLabel={(option) => option.name || option.id}
                                filterOptions={(options, { inputValue }) => {
                                    if (!inputValue || inputValue.length < 2) return [];
                                    const lower = inputValue.toLowerCase();
                                    return options.filter(o => (o.name || '').toLowerCase().includes(lower)).slice(0, 20);
                                }}
                                onChange={(e, value) => { if (value) onActivateAccount(value); }}
                                loading={!!activatingAccount}
                                renderInput={(params) => (
                                    <TextField {...params} label="Search filtered accounts by name..." variant="outlined" size="small" />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box>
                                            <Typography variant='body2'>{option.name || 'Unnamed'}</Typography>
                                            {option.owner && <Typography variant='caption' color='text.secondary'>Owner: {option.owner}</Typography>}
                                        </Box>
                                    </li>
                                )}
                                value={null}
                                blurOnSelect
                                clearOnBlur
                            />
                            {activateSuccess && activateSuccess !== 'error' && (
                                <Alert severity='success' sx={{ mt: 1 }} onClose={() => setActivateSuccess(null)}>
                                    <b>{activateSuccess}</b> has been restored and will now appear in your accounts.
                                </Alert>
                            )}
                            {activateSuccess === 'error' && (
                                <Alert severity='error' sx={{ mt: 1 }} onClose={() => setActivateSuccess(null)}>
                                    Failed to restore account. Please try again.
                                </Alert>
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>
        );
    }

    function renderBookings() {
        return (
            <Box sx={{ p: 3 }}>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                        {bookingsEnabled && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!bookingsEnabled && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                Enable Bookings
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Show the Bookings page in the sidebar. Won opportunities will automatically appear in the bookings pipeline.
                            </Typography>
                        </Box>
                        <Switch checked={bookingsEnabled} onChange={() => setBookingsEnabled(!bookingsEnabled)} />
                    </Box>
                </Paper>
            </Box>
        );
    }

    function renderSignature() {
        return (
            <Box sx={{ p: 3 }}>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                        {signatureEnabled && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!signatureEnabled && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                Email Signatures (premium)
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Each user can set a personal email signature (from their account menu) that is automatically appended to emails they send from Monada. Enable this only for workspaces on the signatures add-on.
                            </Typography>
                        </Box>
                        <Switch checked={signatureEnabled} onChange={() => setSignatureEnabled(!signatureEnabled)} />
                    </Box>
                </Paper>
            </Box>
        );
    }

    function renderFollowUp() {
        return (
            <Box sx={{ p: 3 }}>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                Follow-up Reminder Window
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                A sent quote appears in the home page follow-up banner once it has gone unanswered for at least the
                                minimum number of days, and drops off after the maximum. Lowering the minimum (e.g. to 1 day) surfaces
                                reminders sooner — handy for demos or fast-moving desks.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>From</Typography>
                                <Select size='small' value={followUpMinDays} onChange={(e) => setFollowUpMinDays(e.target.value)} sx={{ minWidth: 90 }}>
                                    {[1, 2, 3, 5, 7, 10, 14].map(d => <MenuItem key={d} value={d}>{d} {d === 1 ? 'day' : 'days'}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>Until</Typography>
                                <Select size='small' value={followUpMaxDays} onChange={(e) => setFollowUpMaxDays(e.target.value)} sx={{ minWidth: 90 }}>
                                    {[7, 14, 21, 30, 45, 60, 90].map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
                                </Select>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        );
    }

    function renderIntegrations() {
        return (
            <Box sx={{p: 3}}>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1 }}>
                        {emailIntegration && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!emailIntegration && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1 }}>
                            Email
                        </Typography>
                        <Switch checked={emailIntegration} onChange={() => setEmailIntegration(!emailIntegration)} />
                    </Box>
                    {emailIntegration && (
                        <Box sx={{ borderTop: `1px solid ${theme.palette.border1}`, p: 3, display: 'flex', flexDirection: 'column' }}>
                            <FormControl>
                                <FormLabel>Email Provider</FormLabel>
                                <RadioGroup
                                    value={_.get(organization, 'integrations.email.supplier', 'outlook')}
                                    onChange={(e) => {
                                        const o = JSON.parse(JSON.stringify(organization));
                                        _.set(o, 'integrations.email.supplier', e.target.value);
                                        setOrganization(o);
                                    }}
                                >
                                    <FormControlLabel value='outlook' control={<Radio />} label='Microsoft Outlook' />
                                    <FormControlLabel value='gmail' control={<Radio />} label='Gmail' />
                                </RadioGroup>
                            </FormControl>
                            <FormControlLabel
                                sx={{ mt: 2 }}
                                control={
                                    <Switch
                                        checked={_.get(organization, 'integrations.email.includeOriginalThread', true)}
                                        onChange={e => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.email.includeOriginalThread', e.target.checked);
                                            setOrganization(o);
                                        }}
                                    />
                                }
                                label='Include original thread in replies by default'
                            />
                        </Box>
                    )}
                </Paper>
                        
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1 }}>
                        {cargowiseIntegration && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!cargowiseIntegration && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1 }}>
                            Cargowise
                        </Typography>
                        <Switch checked={cargowiseIntegration} onChange={() => setCargowiseIntegration(!cargowiseIntegration)} />
                    </Box>
                    {cargowiseIntegration && (
                        <Box sx={{ borderTop: `1px solid ${theme.palette.border1}`, p: 3, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1, mb: 2 }}>
                                Companies
                            </Typography>
                            {(() => {
                                // Get companies array, or create from legacy fields for backward compatibility
                                let companies = _.get(organization, 'integrations.cargowise.companies', []);
                                if (companies.length === 0 && (_.get(organization, 'integrations.cargowise.companyCode') || _.get(organization, 'integrations.cargowise.enterpriseId') || _.get(organization, 'integrations.cargowise.serverId'))) {
                                    // Migrate legacy single company to companies array
                                    companies = [{
                                        companyCode: _.get(organization, 'integrations.cargowise.companyCode', ''),
                                        enterpriseId: _.get(organization, 'integrations.cargowise.enterpriseId', ''),
                                        serverId: _.get(organization, 'integrations.cargowise.serverId', ''),
                                        edocsemail: _.get(organization, 'integrations.cargowise.edocsemail', ''),
                                        branchDepartments: _.get(organization, 'integrations.cargowise.branchDepartments', [])
                                    }];
                                }
                                return companies.map((company, companyIndex) => (
                                    <Paper key={companyIndex} sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant='body1' sx={{ fontWeight: 700 }}>
                                                Company {companyIndex + 1}
                                            </Typography>
                                            {companies.length > 1 && (
                                                <IconButton color='error' onClick={() => {
                                                    const o = JSON.parse(JSON.stringify(organization));
                                                    const companiesList = _.get(o, 'integrations.cargowise.companies', []);
                                                    companiesList.splice(companyIndex, 1);
                                                    _.set(o, 'integrations.cargowise.companies', companiesList);
                                                    setOrganization(o);
                                                }}>
                                                    <FontAwesomeIcon icon={faXmark} />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <TextField
                                            label='Company Code'
                                            variant='outlined'
                                            value={company.companyCode || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                
                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                    companies = [{
                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                    }];
                                                }
                                                
                                                if (!companies[companyIndex]) {
                                                    companies[companyIndex] = {
                                                        companyCode: '',
                                                        enterpriseId: '',
                                                        serverId: '',
                                                        edocsemail: '',
                                                        branchDepartments: []
                                                    };
                                                }
                                                
                                                companies[companyIndex].companyCode = e.target.value;
                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                setOrganization(o);
                                            }}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            label='Enterprise ID'
                                            variant='outlined'
                                            value={company.enterpriseId || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                
                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                    companies = [{
                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                    }];
                                                }
                                                
                                                if (!companies[companyIndex]) {
                                                    companies[companyIndex] = {
                                                        companyCode: '',
                                                        enterpriseId: '',
                                                        serverId: '',
                                                        edocsemail: '',
                                                        branchDepartments: []
                                                    };
                                                }
                                                
                                                companies[companyIndex].enterpriseId = e.target.value;
                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                setOrganization(o);
                                            }}
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            label='Server ID'
                                            variant='outlined'
                                            value={company.serverId || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                
                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                    companies = [{
                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                    }];
                                                }
                                                
                                                if (!companies[companyIndex]) {
                                                    companies[companyIndex] = {
                                                        companyCode: '',
                                                        enterpriseId: '',
                                                        serverId: '',
                                                        edocsemail: '',
                                                        branchDepartments: []
                                                    };
                                                }
                                                
                                                companies[companyIndex].serverId = e.target.value;
                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                setOrganization(o);
                                            }}
                                            sx={{ mb: 2 }}
                                        />
                            <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1, mt: 2 }}>
                                Send to Cargowise as:
                            </Typography>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby='cargowise-intergration-data-target-type'
                                    defaultValue={'ForwardingShipment'}
                                    name='cargowise-data-target-type-group'
                                    value={_.get(organization, 'integrations.cargowise.dataTargetType', 'ForwardingShipment')}
                                    onChange={(e) => {
                                        const o = JSON.parse(JSON.stringify(organization));
                                        _.set(o, 'integrations.cargowise.dataTargetType', e.target.value)
                                        setOrganization(o);
                                    }}
                                >
                                    <FormControlLabel value='ForwardingShipment' control={<Radio />} label='New Shipment' />
                                    <FormControlLabel value='OneOffQuote' control={<Radio />} label='One-off Quote' />
                                </RadioGroup>
                            </FormControl>
                            <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1, mt: 2 }}>
                                Method of Integration
                            </Typography>
                            <FormControl sx={{ mb: 2, mt: 1 }}>
                                <InputLabel id='cargowise-integration-method-label'>Method</InputLabel>
                                <Select
                                    label='Method'
                                    value={_.get(organization, 'integrations.cargowise.method', 'ftp')}
                                    onChange={(e) => {
                                        const o = JSON.parse(JSON.stringify(organization));
                                        _.set(o, 'integrations.cargowise.method', e.target.value)
                                        setOrganization(o);
                                    }}
                                >
                                    <MenuItem value='ftp'>FTP</MenuItem>
                                    <MenuItem value='rest'>REST</MenuItem>
                                    <MenuItem value='soap'>SOAP</MenuItem>
                                    <MenuItem value='display'>Display</MenuItem>
                                </Select>
                            </FormControl>
                            {_.get(organization, 'integrations.cargowise.method', 'ftp') === 'ftp' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <TextField
                                        label='FTP Server'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.ftp.server', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.ftp.server', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='FTP Username'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.ftp.username', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.ftp.username', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='FTP Password'
                                        variant='outlined'
                                        type='password'
                                        value={_.get(organization, 'integrations.cargowise.ftp.password', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.ftp.password', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            )}
                            {_.get(organization, 'integrations.cargowise.method', 'ftp') === 'rest' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <TextField
                                        label='REST URL'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.rest.url', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.rest.url', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='Username'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.rest.username', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.rest.username', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='Password'
                                        variant='outlined'
                                        type='password'
                                        value={_.get(organization, 'integrations.cargowise.rest.password', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.rest.password', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            )}
                            {_.get(organization, 'integrations.cargowise.method', 'ftp') === 'soap' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <TextField
                                        label='WSDL URL'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.soap.wsdlUrl', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.soap.wsdlUrl', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='Client ID'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.soap.clientId', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.soap.clientId', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='Username'
                                        variant='outlined'
                                        value={_.get(organization, 'integrations.cargowise.soap.username', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.soap.username', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label='Password'
                                        variant='outlined'
                                        type='password'
                                        value={_.get(organization, 'integrations.cargowise.soap.password', '')}
                                        onChange={(e) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'integrations.cargowise.soap.password', e.target.value)
                                            setOrganization(o);
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            )}
                                        <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1, mt: 2, mb: 1 }}>
                                            E-docs Mailbox Address
                                        </Typography>
                                        <TextField
                                            label='Email'
                                            variant='outlined'
                                            value={company.edocsemail || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                
                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                    companies = [{
                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                    }];
                                                }
                                                
                                                if (!companies[companyIndex]) {
                                                    companies[companyIndex] = {
                                                        companyCode: '',
                                                        enterpriseId: '',
                                                        serverId: '',
                                                        edocsemail: '',
                                                        branchDepartments: []
                                                    };
                                                }
                                                
                                                companies[companyIndex].edocsemail = e.target.value;
                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                setOrganization(o);
                                            }}
                                            sx={{ mb: 2 }}
                                        />
                                        <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1, mt: 2, mb: 1 }}>
                                            Branches and Departments
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {(company.branchDepartments || []).map((row, index) => (
                                                    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                        <TextField
                                                            sx={{ flex: 1 }}
                                                            label='Branch Name'
                                                            value={row.branchName || ''}
                                                            onChange={(e) => {
                                                                const o = JSON.parse(JSON.stringify(organization));
                                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                                
                                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                                    companies = [{
                                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                                    }];
                                                                }
                                                                
                                                                if (!companies[companyIndex]) {
                                                                    companies[companyIndex] = {
                                                                        companyCode: '',
                                                                        enterpriseId: '',
                                                                        serverId: '',
                                                                        edocsemail: '',
                                                                        branchDepartments: []
                                                                    };
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments) {
                                                                    companies[companyIndex].branchDepartments = [];
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments[index]) {
                                                                    companies[companyIndex].branchDepartments[index] = {
                                                                        branchName: '',
                                                                        branchCode: '',
                                                                        departmentName: '',
                                                                        departmentCode: ''
                                                                    };
                                                                }
                                                                
                                                                companies[companyIndex].branchDepartments[index].branchName = e.target.value;
                                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                                setOrganization(o);
                                                            }}
                                                        />
                                                        <TextField
                                                            sx={{ flex: 1 }}
                                                            label='Branch Code'
                                                            value={row.branchCode || ''}
                                                            onChange={(e) => {
                                                                const o = JSON.parse(JSON.stringify(organization));
                                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                                
                                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                                    companies = [{
                                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                                    }];
                                                                }
                                                                
                                                                if (!companies[companyIndex]) {
                                                                    companies[companyIndex] = {
                                                                        companyCode: '',
                                                                        enterpriseId: '',
                                                                        serverId: '',
                                                                        edocsemail: '',
                                                                        branchDepartments: []
                                                                    };
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments) {
                                                                    companies[companyIndex].branchDepartments = [];
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments[index]) {
                                                                    companies[companyIndex].branchDepartments[index] = {
                                                                        branchName: '',
                                                                        branchCode: '',
                                                                        departmentName: '',
                                                                        departmentCode: ''
                                                                    };
                                                                }
                                                                
                                                                companies[companyIndex].branchDepartments[index].branchCode = e.target.value;
                                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                                setOrganization(o);
                                                            }}
                                                        />
                                                        <TextField
                                                            sx={{ flex: 1 }}
                                                            label='Department Name'
                                                            value={row.departmentName || ''}
                                                            onChange={(e) => {
                                                                const o = JSON.parse(JSON.stringify(organization));
                                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                                
                                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                                    companies = [{
                                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                                    }];
                                                                }
                                                                
                                                                if (!companies[companyIndex]) {
                                                                    companies[companyIndex] = {
                                                                        companyCode: '',
                                                                        enterpriseId: '',
                                                                        serverId: '',
                                                                        edocsemail: '',
                                                                        branchDepartments: []
                                                                    };
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments) {
                                                                    companies[companyIndex].branchDepartments = [];
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments[index]) {
                                                                    companies[companyIndex].branchDepartments[index] = {
                                                                        branchName: '',
                                                                        branchCode: '',
                                                                        departmentName: '',
                                                                        departmentCode: ''
                                                                    };
                                                                }
                                                                
                                                                companies[companyIndex].branchDepartments[index].departmentName = e.target.value;
                                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                                setOrganization(o);
                                                            }}
                                                        />
                                                        <TextField
                                                            sx={{ flex: 1 }}
                                                            label='Department Code'
                                                            value={row.departmentCode || ''}
                                                            onChange={(e) => {
                                                                const o = JSON.parse(JSON.stringify(organization));
                                                                let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                                
                                                                // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                                if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                                    companies = [{
                                                                        companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                                        enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                                        serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                                        edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                                        branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                                    }];
                                                                }
                                                                
                                                                if (!companies[companyIndex]) {
                                                                    companies[companyIndex] = {
                                                                        companyCode: '',
                                                                        enterpriseId: '',
                                                                        serverId: '',
                                                                        edocsemail: '',
                                                                        branchDepartments: []
                                                                    };
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments) {
                                                                    companies[companyIndex].branchDepartments = [];
                                                                }
                                                                
                                                                if (!companies[companyIndex].branchDepartments[index]) {
                                                                    companies[companyIndex].branchDepartments[index] = {
                                                                        branchName: '',
                                                                        branchCode: '',
                                                                        departmentName: '',
                                                                        departmentCode: ''
                                                                    };
                                                                }
                                                                
                                                                companies[companyIndex].branchDepartments[index].departmentCode = e.target.value;
                                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                                setOrganization(o);
                                                            }}
                                                        />
                                                        <IconButton color='error' onClick={() => {
                                                            const o = JSON.parse(JSON.stringify(organization));
                                                            let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                            
                                                            // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                            if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                                companies = [{
                                                                    companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                                    enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                                    serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                                    edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                                    branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                                }];
                                                            }
                                                            
                                                            if (companies[companyIndex] && companies[companyIndex].branchDepartments) {
                                                                companies[companyIndex].branchDepartments.splice(index, 1);
                                                                _.set(o, 'integrations.cargowise.companies', companies);
                                                                setOrganization(o);
                                                            }
                                                        }}>
                                                            <FontAwesomeIcon icon={faXmark} />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Button 
                                                variant='contained' 
                                                color='primary'
                                                onClick={() => {
                                                    const o = JSON.parse(JSON.stringify(organization));
                                                    let companies = _.get(o, 'integrations.cargowise.companies', []);
                                                    
                                                    // Migrate legacy data if companies array doesn't exist but legacy fields do
                                                    if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                                        companies = [{
                                                            companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                                            enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                                            serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                                            edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                                            branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                                        }];
                                                        _.set(o, 'integrations.cargowise.companies', companies);
                                                    }
                                                    
                                                    if (!companies[companyIndex]) {
                                                        companies[companyIndex] = {
                                                            companyCode: '',
                                                            enterpriseId: '',
                                                            serverId: '',
                                                            edocsemail: '',
                                                            branchDepartments: []
                                                        };
                                                    }
                                                    
                                                    if (!companies[companyIndex].branchDepartments) {
                                                        companies[companyIndex].branchDepartments = [];
                                                    }
                                                    
                                                    companies[companyIndex].branchDepartments.push({
                                                        branchName: '',
                                                        branchCode: '',
                                                        departmentName: '',
                                                        departmentCode: ''
                                                    });
                                                    
                                                    _.set(o, 'integrations.cargowise.companies', companies);
                                                    setOrganization(o);
                                                }}
                                                sx={{ alignSelf: 'flex-start', mt: 3 }}
                                            >
                                                + Branch & Department
                                            </Button>
                                        </Box>
                                    </Paper>
                                ));
                            })()}
                            <Button 
                                variant='contained' 
                                color='primary'
                                onClick={() => {
                                    const o = JSON.parse(JSON.stringify(organization));
                                    let companies = _.get(o, 'integrations.cargowise.companies', []);
                                    
                                    // Migrate legacy data if companies array doesn't exist but legacy fields do
                                    if (companies.length === 0 && (_.get(o, 'integrations.cargowise.companyCode') || _.get(o, 'integrations.cargowise.enterpriseId') || _.get(o, 'integrations.cargowise.serverId'))) {
                                        companies = [{
                                            companyCode: _.get(o, 'integrations.cargowise.companyCode', ''),
                                            enterpriseId: _.get(o, 'integrations.cargowise.enterpriseId', ''),
                                            serverId: _.get(o, 'integrations.cargowise.serverId', ''),
                                            edocsemail: _.get(o, 'integrations.cargowise.edocsemail', ''),
                                            branchDepartments: _.get(o, 'integrations.cargowise.branchDepartments', [])
                                        }];
                                    }
                                    
                                    companies.push({
                                        companyCode: '',
                                        enterpriseId: '',
                                        serverId: '',
                                        edocsemail: '',
                                        branchDepartments: []
                                    });
                                    _.set(o, 'integrations.cargowise.companies', companies);
                                    setOrganization(o);
                                }}
                                sx={{ alignSelf: 'flex-start', mt: 2 }}
                            >
                                + Add Company
                            </Button>
                        </Box>
                    )}
                </Paper>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1 }}>
                        {okargoIntegration && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!okargoIntegration && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1 }}>
                            Okargo
                        </Typography>
                        <Switch checked={okargoIntegration} onChange={() => setOkargoIntegration(!okargoIntegration)} />
                    </Box>
                    {okargoIntegration && (
                        <Box sx={{ borderTop: `1px solid ${theme.palette.border1}`, p: 3, display: 'flex', flexDirection: 'column' }}>
                            <TextField
                                label='Token'
                                variant='outlined'
                                value={_.get(organization, 'integrations.okargo.token', '')}
                                onChange={(e) => {
                                    const o = JSON.parse(JSON.stringify(organization));
                                    _.set(o, 'integrations.okargo.token', e.target.value)
                                    setOrganization(o);
                                }}
                                sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, marginRight: '24px' }}>
                                    Online Platforms
                                </Typography>
                                <LoadingButton color='primary' variant='contained' loading={_.some(platformsSettings, v => v.status === 'checking')} onClick={checkOkargoPlatforms} >
                                    Validate
                                </LoadingButton>
                            </Box>
                            {_.get(organization, 'integrations.okargo.platforms', []).map((platform, platformIndex) => (
                                <Box sx={{ mb: 1, mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }} >
                                        <FormControl sx={{ flex: 1, mr: 1 }}>
                                            <InputLabel id='platformid-label'>Platform</InputLabel>
                                            <Select
                                                label='Platform'
                                                value={platform.id}
                                                onChange={(e) => {
                                                    const o = JSON.parse(JSON.stringify(organization));
                                                    _.set(o, `integrations.okargo.platforms[${platformIndex}].id`, e.target.value)
                                                    setOrganization(o);
                                                }}
                                            >
                                                {_(OKARGO_PLATFORMS).keys().sortBy(k => OKARGO_PLATFORMS[k].name).map(k => (
                                                    <MenuItem key={k} value={k}>{OKARGO_PLATFORMS[k].name}</MenuItem>
                                                )).value()}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            sx={{ flex: 1, mr: 1 }}
                                            label='Username'
                                            variant='outlined'
                                            value={platform.username || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                _.set(o, `integrations.okargo.platforms[${platformIndex}].username`, e.target.value)
                                                setOrganization(o);
                                            }}
                                        />
                                        <TextField
                                            sx={{ flex: 1, mr: 1 }}
                                            label='Password'
                                            variant='outlined'
                                            type='password'
                                            value={platform.password || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                _.set(o, `integrations.okargo.platforms[${platformIndex}].password`, e.target.value)
                                                setOrganization(o);
                                            }}
                                        />
                                        <Button
                                            disabled={_.some(platformsSettings, v => v.status === 'checking')}
                                            onClick={() => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                const platforms = _.get(o, 'integrations.okargo.platforms', []);
                                                platforms.splice(platformIndex, 1);
                                                _.set(o, 'integrations.okargo.platforms', platforms);
                                                setOrganization(o);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                    {(platformsSettings[platform.id] || {}).status === 'ok' && (
                                        <Alert
                                            severity='success'
                                            sx={{ marginButtom: '12px' }}
                                            action={
                                                <Button color='inherit' size='small' onClick={() => fixOkargoPlatform({ id: platform.id, settings: platform })}>
                                                    Reconfigure
                                                </Button>
                                            }
                                        >
                                            {platformsSettings[platform.id].description}
                                        </Alert>
                                    )}
                                    {(platformsSettings[platform.id] || {}).status === 'error' && (
                                        <Alert 
                                            severity='error' 
                                            sx={{ marginButtom: '12px' }}
                                            action={
                                                <Button color='inherit' size='small' onClick={() => fixOkargoPlatform({ id: platform.id, settings: platform })}>
                                                    Fix
                                                </Button>
                                            }
                                        >
                                            {platformsSettings[platform.id].description}
                                        </Alert>
                                    )}
                                    {(platformsSettings[platform.id] || {}).status === 'checking' && (
                                        <Alert 
                                            icon={false}
                                            severity='info' 
                                            sx={{ marginButtom: '12px' }}
                                        >
                                            <CircularProgress size={18} />
                                        </Alert>
                                    )}
                                </Box>
                            ))}
                            <Button
                                sx={{ flex: 0, width: '170px' }}
                                variant='contained'
                                disabled={_.some(platformsSettings, v => v.status === 'checking')}
                                onClick={() => {
                                    const o = JSON.parse(JSON.stringify(organization));
                                    const platforms = _.get(o, 'integrations.okargo.platforms', []);
                                    platforms.push({ id: '', username: '', password: '' });
                                    _.set(o, 'integrations.okargo.platforms', platforms);
                                    setOrganization(o);
                                }}>
                                + Add Portal
                            </Button>
                        </Box>
                    )}
                </Paper>
                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1 }}>
                        {webcargoIntegration && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!webcargoIntegration && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700, flex: 1 }}>
                            WebCargo
                        </Typography>
                        <Switch checked={webcargoIntegration} onChange={() => setWebcargoIntegration(!webcargoIntegration)} />
                    </Box>
                    {webcargoIntegration && (
                        <Box sx={{ borderTop: `1px solid ${theme.palette.border1}`, p: 3, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant='body1' sx={{ textDecoration: 'none', fontWeight: 700, marginRight: '24px' }}>
                                    Tokens
                                </Typography>
                            </Box>
                            {_.get(organization, 'integrations.webcargo.tokens', []).map((token, tokenIndex) => (
                                <Box sx={{ mb: 1, mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }} >
                                        <FormControl sx={{ flex: 1, mr: 1 }}>
                                            <InputLabel id='tokencountrycode-label'>Country</InputLabel>
                                            <Select
                                                label='Country'
                                                value={token.countryCode}
                                                onChange={(e) => {
                                                    const o = JSON.parse(JSON.stringify(organization));
                                                    _.set(o, `integrations.webcargo.tokens[${tokenIndex}].countryCode`, e.target.value)
                                                    setOrganization(o);
                                                }}
                                            >
                                                {COUNTRY_LIST.map(k => (
                                                    <MenuItem key={k.code} value={k.code}>{k.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            sx={{ flex: 1, mr: 1 }}
                                            label='Token #1'
                                            variant='outlined'
                                            value={token.token1 || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                _.set(o, `integrations.webcargo.tokens[${tokenIndex}].token1`, e.target.value)
                                                setOrganization(o);
                                            }}
                                        />
                                        <TextField
                                            sx={{ flex: 1, mr: 1 }}
                                            label='Token #2'
                                            variant='outlined'
                                            value={token.token2 || ''}
                                            onChange={(e) => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                _.set(o, `integrations.webcargo.tokens[${tokenIndex}].token2`, e.target.value)
                                                setOrganization(o);
                                            }}
                                        />
                                        <Button
                                            onClick={() => {
                                                const o = JSON.parse(JSON.stringify(organization));
                                                const tokens = _.get(o, 'integrations.webcargo.tokens', []);
                                                tokens.splice(tokenIndex, 1);
                                                _.set(o, 'integrations.webcargo.tokens', tokens);
                                                setOrganization(o);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                            <Button
                                sx={{ flex: 0, width: '170px' }}
                                variant='contained'
                                onClick={() => {
                                    const o = JSON.parse(JSON.stringify(organization));
                                    const tokens = _.get(o, 'integrations.webcargo.tokens', []);
                                    tokens.push({ countryCode: '', token1: '', token2: '' });
                                    _.set(o, 'integrations.webcargo.tokens', tokens);
                                    setOrganization(o);
                                }}>
                                + Add Token
                            </Button>
                        </Box>
                    )}
                </Paper>

                <CargofiveSettings
                    enabled={cargofiveIntegration}
                    setEnabled={setCargofiveIntegration}
                    apiKey={_.get(organization, 'integrations.cargofive.apiKey', '')}
                    setApiKey={(value) => {
                        const o = JSON.parse(JSON.stringify(organization));
                        _.set(o, 'integrations.cargofive.apiKey', value);
                        setOrganization(o);
                    }}
                    useCargofiveForRates={cargofiveUseForRates}
                    setUseCargofiveForRates={setCargofiveUseForRates}
                />

                <CargoAISettings
                    enabled={cargoaiIntegration}
                    setEnabled={setCargoAIIntegration}
                    getConfiguration={(key, def) => _.get(organization, `integrations.cargoai.${key}`, def)}
                    setConfiguration={(key, value) => {
                        const o = JSON.parse(JSON.stringify(organization));
                        _.set(o, `integrations.cargoai.${key}`, value)
                        setOrganization(o);
                    }}
                />

                <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                    <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                        {opportunityWebhookEnabled && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                        {!opportunityWebhookEnabled && <Chip label='Inactive' sx={{ mr: 2 }} />}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                Opportunity Webhook
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Push opportunity events to an external URL in real time.
                            </Typography>
                        </Box>
                        <Switch checked={opportunityWebhookEnabled} onChange={() => setOpportunityWebhookEnabled(!opportunityWebhookEnabled)} />
                    </Box>
                    {opportunityWebhookEnabled && (
                        <Box sx={{ borderTop: `1px solid ${theme.palette.border1}`, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

                            {/* Endpoint URL */}
                            <TextField
                                label='Endpoint URL'
                                variant='outlined'
                                fullWidth
                                value={_.get(organization, 'integrations.opportunityWebhook.url', '')}
                                onChange={(e) => {
                                    const o = JSON.parse(JSON.stringify(organization));
                                    _.set(o, 'integrations.opportunityWebhook.url', e.target.value);
                                    setOrganization(o);
                                }}
                                placeholder='https://your-system.com/api/webhooks'
                                helperText='Monada will POST the event payload to this URL.'
                            />

                            {/* Secret key */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <TextField
                                    label='Secret Key'
                                    variant='outlined'
                                    fullWidth
                                    type={webhookSecretVisible ? 'text' : 'password'}
                                    value={_.get(organization, 'integrations.opportunityWebhook.secret', '')}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton size='small' onClick={() => setWebhookSecretVisible(v => !v)}>
                                                    <FontAwesomeIcon icon={webhookSecretVisible ? icons.faEyeSlash : icons.faEye} />
                                                </IconButton>
                                                <IconButton
                                                    size='small'
                                                    disabled={!_.get(organization, 'integrations.opportunityWebhook.secret')}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(_.get(organization, 'integrations.opportunityWebhook.secret', ''));
                                                        setWebhookCopied(true);
                                                        setTimeout(() => setWebhookCopied(false), 2000);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={webhookCopied ? faCheck : icons.faCopy} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    helperText='Use this to verify incoming requests via HMAC-SHA256. Keep it secret.'
                                />
                                <Button
                                    variant='outlined'
                                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                                    onClick={() => {
                                        const arr = new Uint8Array(32);
                                        crypto.getRandomValues(arr);
                                        const secret = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
                                        const o = JSON.parse(JSON.stringify(organization));
                                        _.set(o, 'integrations.opportunityWebhook.secret', secret);
                                        setOrganization(o);
                                        setWebhookSecretVisible(true);
                                    }}
                                >
                                    {_.get(organization, 'integrations.opportunityWebhook.secret') ? 'Regenerate' : 'Generate Secret'}
                                </Button>
                            </Box>

                            {/* Event subscriptions */}
                            <Box>
                                <Typography variant='body1' sx={{ fontWeight: 700, mb: 1 }}>
                                    Events
                                </Typography>
                                {[
                                    { key: 'opportunity.sent', description: 'Fired when a quote is sent to a customer.' },
                                    { key: 'opportunity.accepted', description: 'Fired when a customer accepts a quote.' },
                                    { key: 'opportunity.won', description: 'Fired when an opportunity is marked as Won.' },
                                ].map(({ key, description }) => (
                                    <FormControlLabel
                                        key={key}
                                        sx={{ display: 'flex' }}
                                        control={
                                            <Switch
                                                checked={_.get(organization, 'integrations.opportunityWebhook.events', []).includes(key)}
                                                onChange={(e) => {
                                                    const o = JSON.parse(JSON.stringify(organization));
                                                    const events = [..._.get(o, 'integrations.opportunityWebhook.events', [])];
                                                    if (e.target.checked) {
                                                        if (!events.includes(key)) events.push(key);
                                                    } else {
                                                        const idx = events.indexOf(key);
                                                        if (idx > -1) events.splice(idx, 1);
                                                    }
                                                    _.set(o, 'integrations.opportunityWebhook.events', events);
                                                    setOrganization(o);
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant='body2' sx={{ fontWeight: 600 }}>{key}</Typography>
                                                <Typography variant='caption' color='text.secondary'>{description}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Box>

                        </Box>
                    )}
                </Paper>

                {me.email === 'admin@monada.ai' && (
                    <Paper sx={{ mt: 3, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                        <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                            {doorRatesEnabled && <Chip label='Active' color='chip-sent-to-endcustomer' sx={{ mr: 2 }} />}
                            {!doorRatesEnabled && <Chip label='Inactive' sx={{ mr: 2 }} />}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                    Door Rates
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                    Enable port/door drayage (metro) rates: adds Land to the rate search and a suburb/postcode door location picker.
                                </Typography>
                            </Box>
                            <Switch checked={doorRatesEnabled} onChange={() => setDoorRatesEnabled(!doorRatesEnabled)} />
                        </Box>
                    </Paper>
                )}
            </Box>
        )
    }

    function renderSettings() {
        const errorsType = errors.map(e => e.type);
        return (
            <Box sx={{p: 3}}>
                <TableContainer>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ width: '20%' }}>
                                    Name of your Organization
                                </TableCell>
                                <TableCell sx={{ width: '60%' }}>
                                    <TextField
                                        variant='outlined'
                                        value={organization.title}
                                        onChange={(e) => setOrganization({ ...organization, title: e.target.value })}
                                        fullWidth
                                    />
                                    {errorsType.includes('title') && (
                                        <Typography variant='caption' color='error'>
                                            Title must contain at least one letter.
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <Typography variant='caption'>
                                        We display your organization name in emails, price quotes, and various other places.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    URL of organization
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        variant='outlined'
                                        value={organization.alias}
                                        onChange={(e) => setOrganization({ ...organization, alias: e.target.value })}
                                        fullWidth
                                    />
                                    {errorsType.includes('alias') && (
                                        <Typography variant='caption' color='error'>
                                            Must be a valid URL
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the URL used to access your system
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Address
                                </TableCell>
                                <TableCell>
                                    <AddressInput
                                        transportationMethod={'land'}
                                        value={_.get(organization, 'address.text', '')}
                                        textFieldProps={{
                                            label: 'Address',
                                        }}
                                        onChange={(v) => {
                                            const o = JSON.parse(JSON.stringify(organization));
                                            _.set(o, 'address', v);
                                            setOrganization(o);
                                        }}
                                        seaPorts={seaPorts}
                                    />
                                    {errorsType.includes('address') && (
                                        <Typography variant='caption' color='error'>
                                            Please enter a valid address.
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the address of your organization. This will help us determine if shipments should be categorized as export or import.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Export XML Mailing List
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        variant='outlined'
                                        value={organization.xmlMailingList || ''}
                                        onChange={(e) => setOrganization({ ...organization, xmlMailingList: e.target.value })}
                                        fullWidth
                                    />
                                    {errorsType.includes('xmlMailingList') && (
                                        <Typography variant='caption' color='error'>
                                            Please enter a valid email address.
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        Comma separated list of emails that will receive every exported XML email sent (used normally for automatic tools)
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Default Currency
                                </TableCell>
                                <TableCell>
                                    <Select
                                        fullWidth
                                        value={organization.defaults?.currency || ''}
                                        onChange={e => setOrganization({ ...organization, defaults: { ...organization.defaults, currency: e.target.value || null } })}
                                    >
                                        {currencies.map(k => (
                                            <MenuItem key={k.code} value={k.code}>{k.name} ({k.code})</MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the default currency for your organization. All prices will be initially displayed in this currency.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Quick Currency Selectors
                                </TableCell>
                                <TableCell>
                                    <Select
                                        fullWidth
                                        value={(organization.defaults?.extraCurrencies || '').split(',')[0]}
                                        onChange={e => setOrganization({ 
                                            ...organization, 
                                            defaults: { 
                                                ...organization.defaults, 
                                                extraCurrencies:[
                                                    e.target.value || '',
                                                    (organization.defaults?.extraCurrencies || '').split(',')[1] || '',
                                                    (organization.defaults?.extraCurrencies || '').split(',')[2] || '',
                                                ].join(',')
                                            } 
                                        })}
                                    >
                                        {currencies.map(k => (
                                            <MenuItem key={k.code} value={k.code}>{k.name} ({k.code})</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        sx={{ margin: '3px 0px' }}
                                        fullWidth
                                        value={(organization.defaults?.extraCurrencies || '').split(',')[1]}
                                        onChange={e => setOrganization({ 
                                            ...organization, 
                                            defaults: { 
                                                ...organization.defaults, 
                                                extraCurrencies:[
                                                    (organization.defaults?.extraCurrencies || '').split(',')[0] || '',
                                                    e.target.value || '',
                                                    (organization.defaults?.extraCurrencies || '').split(',')[2] || '',
                                                ].join(',')
                                            } 
                                        })}
                                    >
                                        {currencies.map(k => (
                                            <MenuItem key={k.code} value={k.code}>{k.name} ({k.code})</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        fullWidth
                                        value={(organization.defaults?.extraCurrencies || '').split(',')[2]}
                                        onChange={e => setOrganization({ 
                                            ...organization, 
                                            defaults: { 
                                                ...organization.defaults, 
                                                extraCurrencies:[
                                                    (organization.defaults?.extraCurrencies || '').split(',')[0] || '',
                                                    (organization.defaults?.extraCurrencies || '').split(',')[1] || '',
                                                    e.target.value || '',
                                                ].join(',')
                                            } 
                                        })}
                                    >
                                        {currencies.map(k => (
                                            <MenuItem key={k.code} value={k.code}>{k.name} ({k.code})</MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        These are the top currencies to see as selection options in the currency selector
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Default Transportation Method
                                </TableCell>
                                <TableCell>
                                    <FormControl>
                                        <FormLabel id='default-transportationMethod-label'>Default Transportation Method</FormLabel>
                                        <RadioGroup
                                            aria-labelledby='default-transportationMethod-label'
                                            defaultValue={null}
                                            name='default-transportationMethod-group'
                                            value={_.get(organization, 'defaults.transportationMethod', null)}
                                            onChange={e => setOrganization({ ...organization, defaults: { ...organization.defaults, transportationMethod: e.target.value || null } })}
                                            row
                                        >
                                            <FormControlLabel value={null} control={<Radio />} label='None' />
                                            <FormControlLabel value='sea' control={<Radio />} label='Sea' />
                                            <FormControlLabel value='air' control={<Radio />} label='Air' />
                                            <FormControlLabel value='land' control={<Radio />} label='Land' />
                                        </RadioGroup>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the default transportation method for your organization.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Default Air Port
                                </TableCell>
                                <TableCell>
                                    <FormControl>
                                        <FormLabel id='default-transportationMethod-air-label'>Default Air Port</FormLabel>
                                        <RadioGroup
                                            aria-labelledby='default-transportationMethod-air-label'
                                            defaultValue={null}
                                            name='default-transportationMethod-air-group'
                                            value={_.get(organization, 'defaults.source.air', null) ? 'yes' : 'no'}
                                            onChange={e => setOrganization({ ...organization, defaults: { ...organization.defaults, source: { ...organization.defaults.source, air: e.target.value === 'yes' ? {} : null } } })}
                                            row
                                        >
                                            <FormControlLabel value={'no'} control={<Radio />} label='None' />
                                            <FormControlLabel value={'yes'} control={<Radio />} label={
                                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span>
                                                        The following port:&nbsp;
                                                    </span>
                                                    <FormControl variant='outlined'>
                                                        <AddressInput
                                                            disabled={_.get(organization, 'defaults.source.air', null) === null}
                                                            transportationMethod={'air'}
                                                            value={_.get(organization, 'defaults.source.air.text', '')}
                                                            textFieldProps={{
                                                                style: { borderRadius: '0px' },
                                                                placeholder: 'Default air port',
                                                                id: 'where-from-air',
                                                                startAdornment: (
                                                                    <InputAdornment position='start'>
                                                                        <LocationOnIcon />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            onChange={(v) => {
                                                                _.set(organization, 'defaults.source.air', v);
                                                            }}
                                                            seaPorts={seaPorts}
                                                        />
                                                    </FormControl>
                                                </span>
                                            } />
                                        </RadioGroup>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the default air port for your organization.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Default Sea Port
                                </TableCell>
                                <TableCell>
                                    <FormControl>
                                        <FormLabel id='default-transportationMethod-sea-label'>Default Sea Port</FormLabel>
                                        <RadioGroup
                                            aria-labelledby='default-transportationMethod-sea-label' 
                                            defaultValue={null}
                                            name='default-transportationMethod-sea-group'
                                            value={_.get(organization, 'defaults.source.sea', null) ? 'yes' : 'no'}
                                            onChange={e =>
                                                setOrganization({
                                                    ...organization,
                                                    defaults: {
                                                        ...organization.defaults,
                                                        source: {
                                                            ...(organization.defaults || {}).source,
                                                            sea: e.target.value === 'yes' ? {} : null

                                                        }
                                                    }
                                                })}
                                            row
                                        >
                                            <FormControlLabel value={'no'} control={<Radio />} label='None' />
                                            <FormControlLabel value={'yes'} control={<Radio />} label={
                                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span>
                                                        The following port:&nbsp;
                                                    </span>
                                                    <FormControl variant='outlined'>
                                                        <AddressInput
                                                            disabled={_.get(organization, 'defaults.source.sea', null) === null}
                                                            transportationMethod={'sea'}
                                                            value={_.get(organization, 'defaults.source.sea.text', '')}
                                                            textFieldProps={{
                                                                style: { borderRadius: '0px' },
                                                                placeholder: 'Default sea port',
                                                                id: 'where-from-sea',
                                                                startAdornment: (
                                                                    <InputAdornment position='start'>
                                                                        <LocationOnIcon />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            onChange={(v) => {
                                                                _.set(organization, 'defaults.source.sea', v);
                                                            }}
                                                            seaPorts={seaPorts}
                                                        />
                                                    </FormControl>
                                                </span>
                                            } />
                                        </RadioGroup>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the default sea port for your organization.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Default Land Port
                                </TableCell>
                                <TableCell>
                                    <FormControl>
                                        <FormLabel id='default-transportationMethod-land-label'>Default Land Port</FormLabel>
                                        <RadioGroup
                                            aria-labelledby='default-transportationMethod-land-label'
                                            defaultValue={null}
                                            name='default-transportationMethod-land-group'
                                            value={_.get(organization, 'defaults.source.land', null) ? 'yes' : 'no'}
                                            onChange={e =>
                                                setOrganization({
                                                    ...organization,
                                                    defaults: {
                                                        ...organization.defaults,
                                                        source: {
                                                            ...(organization.defaults || {}).source,
                                                            land: e.target.value === 'yes' ? {} : null

                                                        }
                                                    }
                                                })}
                                            row
                                        >
                                            <FormControlLabel value={'no'} control={<Radio />} label='None' />
                                            <FormControlLabel value={'yes'} control={<Radio />} label={
                                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span>
                                                        The following port:&nbsp;
                                                    </span>
                                                    <FormControl variant='outlined'>
                                                        <AddressInput
                                                            disabled={_.get(organization, 'defaults.source.land', null) === null}
                                                            transportationMethod={'land'}
                                                            value={_.get(organization, 'defaults.source.land.text', '')}
                                                            textFieldProps={{
                                                                style: { borderRadius: '0px' },
                                                                placeholder: 'Default land port',
                                                                id: 'where-from-land',
                                                                startAdornment: (
                                                                    <InputAdornment position='start'>
                                                                        <LocationOnIcon />
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            onChange={(v) => {
                                                                _.set(organization, 'defaults.source.land', v);
                                                            }}
                                                            seaPorts={seaPorts}
                                                        />
                                                    </FormControl>
                                                </span>
                                            } />
                                        </RadioGroup>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This is the default land port for your organization.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Timezone
                                </TableCell>
                                <TableCell>
                                    <Select
                                        labelId='timezone-select-label'
                                        value={organization.timezone || 'UTC'}
                                        onChange={(e) => setOrganization({ ...organization, timezone: e.target.value })}
                                        fullWidth
                                    >
                                        {Intl.supportedValuesOf('timeZone').map(tz => (
                                            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This timezone will be used for displaying dates and times across the application
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Round chargeable weight (air)
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        checked={organization.airRoundChargeableWeight}
                                        onChange={(e) => setOrganization({ ...organization, airRoundChargeableWeight: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        This will round the chargeable weight to the closest 0.5kg for air shipments. When enabled, the chargeable weight is calculated and displayed only for the entire shipment, not per product.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    Default View Template (Debug)
                                </TableCell>
                                <TableCell>
                                    <Select
                                        fullWidth
                                        value={organization.defaults?.viewTemplate || 'standard'}
                                        onChange={e => setOrganization({ ...organization, defaults: { ...organization.defaults, viewTemplate: e.target.value || 'standard' } })}
                                    >
                                        <MenuItem value='standard'>Standard</MenuItem>
                                        <MenuItem value='contract'>Contract</MenuItem>
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='caption'>
                                        Default view template to use when creating new opportunities
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    async function onSave() {
        const _errors = [];

        if (!_.trim(organization.title)) {
            _errors.push({type: 'title', msg: 'You are missing a title for the organization.' });
        }

        if (!_.trim(organization.alias)) {
            _errors.push({ type: 'alias', msg: 'You are missing an alias (URL) for the organization.' });
        } else if (!validURL(`https://app.monada.ai/${organization.alias}`)) {
            _errors.push({ type: 'alias', msg: 'The alias (URL) you entered is not a valid one.' });
        }

        const xmlMailingList = _.trim(organization.xmlMailingList) ? _.trim(organization.xmlMailingList).split(',') : [];
        if (!_.isEmpty(xmlMailingList) && _.some(xmlMailingList, e => !validator.validate(e))) {
            _errors.push({ type: 'xmlMailingList', msg: 'The XML mailing list contains some invalid emails.' });
        }

        setErrors(_errors);

        if (!_.isEmpty(_errors)) return;

        setSaving(true);

        if (organization.defaults.transportationMethod === null) {
            delete organization.defaults.transportationMethod;
        }

        try {
            const rr = _.cloneDeep(reportConfiguration);
            rr.forEach(page => page.fields.forEach(report => delete report.func));
            const {data: newReportConfiguration} = await axios.post(`/api/reportconfiguration?organizationId=${organization.id}&accesstoken=${accesstoken}`,
                { reports: rr }
            );
            setReportConfiguration(newReportConfiguration);
            // Clean up legacy branchDepartments
            const branchDepartments = _.get(organization, 'integrations.cargowise.branchDepartments', []);
            _.set(organization, 'integrations.cargowise.branchDepartments', branchDepartments.filter(e => !!_.trim(e.branchName) || !!_.trim(e.branchCode) || !!_.trim(e.departmentName) || !!_.trim(e.departmentCode)));
            
            // Clean up branchDepartments within companies
            const companies = _.get(organization, 'integrations.cargowise.companies', []);
            companies.forEach((company, index) => {
                if (company.branchDepartments) {
                    const cleaned = company.branchDepartments.filter(e => !!_.trim(e.branchName) || !!_.trim(e.branchCode) || !!_.trim(e.departmentName) || !!_.trim(e.departmentCode));
                    _.set(organization, `integrations.cargowise.companies[${index}].branchDepartments`, cleaned);
                }
            });
            if (organization.integrations?.cargowise?.branches) {
                delete organization.integrations.cargowise.branches;
            }

            const {data: updated} = await axios.post(`/api/organizations/${organization.id}/patch?accesstoken=${accesstoken}`, {
                organization: {
                    alias: organization.alias,
                    title: organization.title,
                    timezone: organization.timezone,
                    xmlMailingList: xmlMailingList,
                    defaults: organization.defaults,
                    integrations: organization.integrations,
                    airRoundChargeableWeight: organization.airRoundChargeableWeight,
                    exchangeRates: organization.exchangeRates?.map(r => ({
                        ..._.omit(r, ['id']),
                        rate: parseFloat(r.rate)
                    })),
                    ...(organization.address ? { address: organization.address } : {}),
                    settings: organization.settings,
                }
            });
            // Ensure viewTemplate is included in defaults
            if (organization.defaults?.viewTemplate) {
                updated.defaults = { ...updated.defaults, viewTemplate: organization.defaults.viewTemplate };
            }
            onOrgUpdated(updated);

            setSaving(false);
            setSavingSuccess('yes');
        } catch (e) {
            setSaving(false);
            setSavingSuccess('no');
            setErrors([{msg: e.response?.data?.detail || e.message, type: 'error'}]);
            console.log('Error saving:', e);
        }

        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    return (
        <Box>
            <Header
                icon={icons.faCogs}
                title={'Settings'}
                noMarginBottom={true}
                buttons={[{
                    title: 'Save',
                    icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : null,
                    iconColor: savingSuccess === 'yes' ? colors.green[500] : savingSuccess === 'no' ? colors.red[500] : null,
                    width: '80px',
                    color: 'monada-button',
                    action: onSave,
                    thinking: saving,
                    disabled: saving || !!savingSuccess,
                }]}
            />
            <Tabs
                value={screen}
                onChange={(e, v) => setScreen(v)}
                variant='scrollable'
                scrollButtons='auto'
                allowScrollButtonsMobile
                sx={{
                    minHeight: '40px',
                    background: theme.palette.panel1,
                    borderBottom: `1px solid ${theme.palette.border1}`,
                    marginBottom: '12px',
                    '& .MuiTab-root': { textTransform: 'none', minHeight: '40px', fontSize: '13px', px: '14px' },
                }}
            >
                <Tab value='general' iconPosition='start' icon={<FontAwesomeIcon icon={icons.faGear} />} label='General' />
                <Tab value='exchangerates' iconPosition='start' icon={<FontAwesomeIcon icon={faDollarSign} />} label='Manual Exchange Rates' />
                <Tab value='integrations' iconPosition='start' icon={<FontAwesomeIcon icon={faLink} />} label='Integrations' />
                <Tab value='defaultstrings' iconPosition='start' icon={<FontAwesomeIcon icon={faHeading} />} label='Default Strings' />
                <Tab value='accounts' iconPosition='start' icon={<FontAwesomeIcon icon={icons.faBuilding} />} label='Accounts' />
                <Tab value='bookings' iconPosition='start' icon={<FontAwesomeIcon icon={icons.faShip} />} label='Bookings' />
                <Tab value='followup' iconPosition='start' icon={<FontAwesomeIcon icon={icons.faBell} />} label='Follow-up' />
                {me.email === 'admin@monada.ai' && <Tab value='stats' iconPosition='start' icon={<FontAwesomeIcon icon={icons.faChartLineUp} />} label='Homepage Metrics' />}
                {me.email === 'admin@monada.ai' && <Tab value='signature' iconPosition='start' icon={<FontAwesomeIcon icon={icons.faEnvelope} />} label='Signatures' />}
            </Tabs>
            {errors.length > 0 && (
                <Alert severity='error' sx={{ marginBottom: '12px' }}>
                    <div>
                        Sorry, there was an error saving:<br/>
                    </div>
                    <ul>
                        {errors.map(e => (
                            <li key={e.type}>{e.msg}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            {screen === 'general' && renderSettings()}
            {screen === 'exchangerates' && renderExchangeRates()}
            {screen === 'integrations' && renderIntegrations()}
            {screen === 'defaultstrings' && renderDefaultStrings()}
            {screen === 'accounts' && renderAccounts()}
            {screen === 'bookings' && renderBookings()}
            {screen === 'followup' && renderFollowUp()}
            {screen === 'signature' && renderSignature()}
            {screen === 'stats' && renderStats({ reportConfiguration, setReportConfiguration })}
        </Box>
    );
}

function validURL(str) {
    var pattern = new RegExp('^(https?:\\/\\/)?'+ // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
        '(\\#[-a-z\\d_]*)?$','i'); // fragment locator
    return !!pattern.test(str);
}

export default Settings;

const COUNTRY_LIST = (() => {
    const A = 65;
    const Z = 90;
    const countryName = new Intl.DisplayNames(['en'], { type: 'region' });
    const countries = {}
    for(let i=A; i<=Z; ++i) {
        for(let j=A; j<=Z; ++j) {
            let code = String.fromCharCode(i) + String.fromCharCode(j)
            let name = countryName.of(code)
            if (code !== name) {
                countries[code] = {code, name}
            }
        }
    }
    return _.sortBy(_.values(countries), v => v.name);
})();

function renderStats(props) {
    const { reportConfiguration, setReportConfiguration } = props;

    function setField({ page, field, property, value }) {
        const _reportConfiguration = [...reportConfiguration];
        _reportConfiguration[page].fields[field][property] = value;
        setReportConfiguration(_reportConfiguration);
    }

    if (!reportConfiguration) return (
        <Box sx={{ padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ padding: '12px' }}>
            {reportConfiguration?.map((page, pageIndex) => (
                <Box key={pageIndex} sx={{ marginBottom: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                        <Typography variant='h6'>
                            Title of the page:
                        </Typography>
                        <TextField value={page.title} sx={{ marginLeft: '12px' }} onChange={e => {
                            const _reportConfiguration = [...reportConfiguration];    
                            _reportConfiguration[pageIndex].title = e.target.value;
                            setReportConfiguration(_reportConfiguration);
                        }} />
                        <IconButton color='error' onClick={() => {
                            const _reportConfiguration = [...reportConfiguration];
                            _reportConfiguration.splice(pageIndex, 1);
                            setReportConfiguration(_reportConfiguration);
                        }}>
                            <FontAwesomeIcon icon={icons.faTrash} />
                        </IconButton>
                    </Box>
                    {page.fields.map((field, fieldIndex) => (
                        <Paper key={fieldIndex} sx={{ padding: '12px', marginTop: '12px', marginLeft: '24px' }}>
                            <Typography variant='body1' component='Box'>
                                Title of the block: 
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center'}}>
                                <TextField fullWidth value={field.title} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'title', value: e.target.value })} />
                                <IconButton color='error' onClick={() => {
                                    const _reportConfiguration = [...reportConfiguration];
                                    _reportConfiguration[pageIndex].fields.splice(fieldIndex, 1);
                                    setReportConfiguration(_reportConfiguration);
                                }}>
                                    <FontAwesomeIcon icon={icons.faTrash} />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '12px' }}>
                                <Box>
                                    <Typography variant='body1' component={Box}>
                                        Icon to display:
                                    </Typography>
                                    <TextField fullWidth value={field.icon} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'icon', value: e.target.value })} />
                                    <Typography variant='body1' component={Box} sx={{ paddingTop: '12px' }}>
                                        Color of the icon:
                                    </Typography>
                                    <TextField fullWidth value={field.iconColor} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'iconColor', value: e.target.value })} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '60px' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: colors[field.iconColor]?.[100] || 'black',
                                        color: colors[field.iconColor]?.[500] || 'white',
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '12px',
                                        marginLeft: '12px',
                                        fontSize: '2.4em'
                                    }}>
                                        <FontAwesomeIcon icon={icons[field.icon]} />
                                    </Box>
                                </Box>
                            </Box>
                            <Typography variant='caption' component={Box} sx={{ paddingTop: '12px' }}>
                                Write the javascript body, for the function which has a header of: getStats(accounts, since, until, prevSince, prevUntil). The return value should be an object containing the following fields: mainStats, prevPeriodStats, smaller text: 
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                The stats should calculate:
                                            </TableCell>
                                            <TableCell sx={{ width: '100%' }}>
                                                <TextField fullWidth value={field.calculation} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'calculation', value: e.target.value })} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                The smaller text should be:
                                            </TableCell>
                                            <TableCell sx={{ width: '100%' }}>
                                                <TextField fullWidth value={field.caption} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'caption', value: e.target.value })} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                Text that should be displayed on hover:
                                            </TableCell>
                                            <TableCell sx={{ width: '100%' }}>
                                                <TextField fullWidth value={field.onHoverText} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'onHoverText', value: e.target.value })} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                Navigate link:
                                            </TableCell>
                                            <TableCell sx={{ width: '100%' }}>
                                                <TextField fullWidth value={field.navigateLink} onChange={e => setField({ page: pageIndex, field: fieldIndex, property: 'navigateLink', value: e.target.value })} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                The function generated by the AI:
                                            </TableCell>
                                            <TableCell sx={{ width: '100%' }}>
                                                {field.func ? <pre style={{ fontSize: '12px', fontFamily: 'monospace' }}>{field.func}</pre> : 'No function generated yet. Save the settings to generate it.'}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ))}
                    <Button 
                        sx={{ marginTop: '12px', marginLeft: '24px' }}
                        variant='outlined' 
                        color='primary' 
                        onClick={() => setReportConfiguration(reportConfiguration => {
                            const newReportConfiguration = [...reportConfiguration];
                            newReportConfiguration[pageIndex].fields.push({ title: 'New Stat ' + newReportConfiguration[pageIndex].fields.length + 1 });
                            return newReportConfiguration;
                        })}
                    >
                        + Stat in this page
                    </Button>
                </Box>
            ))}
            <Box sx={{ marginTop: '48px' }}>
                <Button 
                    variant='contained' 
                    color='primary'
                    onClick={() => setReportConfiguration([...reportConfiguration, { title: 'New Page ' + reportConfiguration.length + 1, fields: [] }])}
                >
                    + Page
                </Button>
            </Box>
        </Box>
    )
}